import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["shouldFetchTeams"];
import { useCallback, useMemo } from 'react';
import { fromJS } from 'immutable';
import styled from 'styled-components';
import { ChannelTypes } from 'conversations-internal-schema/constants/ChannelTypes';
import { HELPDESK } from '../../shared/constants/workspaces';
import { useIsUngatedForHelpDeskRoutingScopeFiltering, useIsUngatedForUserAwareRoutingRules } from '../../auth/hooks/useGates';
import { isValid } from '../operators/isValid';
import { checkCanHaveFallback, checkHasUsersAndTeams, getDistribution } from '../operators/routingRulesGetters';
import { setDistribution, setUsersAndTeams } from '../operators/routingRulesSetters';
import { getChannelRoutingDistribution } from '../utils/distribution';
import { DISTRIBUTIONS } from '../constants/distributions';
import AssignToAvailableCheckbox from './AssignToAvailableCheckbox';
import { DistributionSelect } from './DistributionSelect';
import FallbackRow from './FallbackRow';
import RoundRobinRow from './RoundRobinRow';
import RoutingRulesSelectRow from './RoutingRulesSelectRow';
import RoutingRulesFetchTeams from './RoutingRulesFetchTeams';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const RoutingRulesWrapper = styled.div.withConfig({
  displayName: "RoutingRules__RoutingRulesWrapper",
  componentId: "sscouf-0"
})(["width:100%;"]);
const DeprecatedRoutingRules = ({
  aiChatbotId,
  canSelectDistribution,
  currentUser,
  disabled,
  disableKeepAssigneeOption,
  FormControl,
  hasRoutingAccess,
  hasEditAccess,
  hideAvailableUserCheckbox,
  inboxId,
  isLoadingUsersAndTeams,
  isUngatedForHelpDeskRoutingScopeFiltering,
  onOpenChange,
  onRuleUpdate: onRuleUpdateProp,
  onUsersAndTeamsMount,
  overrides = {},
  routingRules,
  showAnyUserOption,
  showKeepAssigneeOption,
  showOwnerOption = true,
  showSelectionRow = true,
  showTeamsOnlyOption = false,
  showUnassignedOption = true,
  teams,
  teamTree,
  trackUsage = () => {},
  use,
  userAndTeamSelectValueComponent,
  users,
  workSpace
}) => {
  const immutableUser = fromJS(currentUser);
  const usersAndTeamsSelectError = !isValid(routingRules);
  const isUngatedForUserAwareRoutingRules = useIsUngatedForUserAwareRoutingRules();
  const isUngatedForScopeFiltering = useIsUngatedForHelpDeskRoutingScopeFiltering();
  const shouldUseScopeFiltering = isUngatedForUserAwareRoutingRules ? isUngatedForScopeFiltering : isUngatedForHelpDeskRoutingScopeFiltering;

  //In the future, we will be removing hasEditAcccess and hasRoutingAccess in favor of user just providing this as part of the 'disabled' prop.
  const lacksScope = shouldUseScopeFiltering && workSpace === HELPDESK ? !hasEditAccess : !hasRoutingAccess;
  const isDisabled = disabled || lacksScope;
  const anyAvailableUser = use === ChannelTypes.CHAT || use === ChannelTypes.FACEBOOK_MESSENGER;
  const defaultDistribution = getChannelRoutingDistribution(use);
  const showFallbackRow = checkCanHaveFallback(routingRules);
  const showRoundRobinRow = checkHasUsersAndTeams(routingRules);
  const showDistributionRow = canSelectDistribution && defaultDistribution === DISTRIBUTIONS.RANDOM;
  const setChannelDistribution = useMemo(
  // this reduces calls to `getChannelRoutingDistribution` and also
  // allows for better typing then call `setDistribution(distribution, routingRules)`
  () => setDistribution(defaultDistribution), [defaultDistribution]);
  const onRuleUpdate = useMemo(() => {
    if (showDistributionRow) {
      return onRuleUpdateProp;
    }
    return newRoutingRules => onRuleUpdateProp(setChannelDistribution(newRoutingRules));
  }, [onRuleUpdateProp, setChannelDistribution, showDistributionRow]);
  const onChange = useCallback((newUserIds, newTeamIds) => {
    // we do a non-null assertion because this can only be called by
    // `RoundRobinRow` which only shows when the routing rules have users and teams
    // meaning routingRules is defined
    onRuleUpdate(setUsersAndTeams(routingRules, newUserIds, newTeamIds));
  }, [onRuleUpdate, routingRules]);
  return /*#__PURE__*/_jsxs(RoutingRulesWrapper, {
    children: [showSelectionRow && /*#__PURE__*/_jsx(RoutingRulesSelectRow, {
      aiChatbotId: aiChatbotId !== null && aiChatbotId !== void 0 ? aiChatbotId : undefined,
      anyAvailableUser: anyAvailableUser,
      disabled: isDisabled,
      disableKeepAssigneeOption: disableKeepAssigneeOption,
      FormControl: FormControl,
      overrides: overrides.routingTypeSelect,
      showAnyUserOption: showAnyUserOption,
      showKeepAssigneeOption: showKeepAssigneeOption,
      showTeamsOnlyOption: showTeamsOnlyOption,
      showUnassignedOption: showUnassignedOption,
      showOwnerOption: showOwnerOption,
      routingRules: routingRules,
      onOpenChange: onOpenChange,
      onRuleUpdate: onRuleUpdate,
      workSpace: workSpace,
      isUngatedForHelpDeskRoutingScopeFiltering: shouldUseScopeFiltering
    }), showFallbackRow && /*#__PURE__*/_jsx(FallbackRow, {
      disabled: isDisabled,
      FormControl: FormControl,
      routingRules: routingRules,
      onRuleUpdate: onRuleUpdate,
      isUngatedForHelpDeskRoutingScopeFiltering: shouldUseScopeFiltering
    }), showRoundRobinRow && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(RoundRobinRow, {
        routingRules: routingRules,
        disabled: isDisabled,
        FormControl: FormControl,
        onUserOrTeamSelect: onChange,
        userAndTeamSelectValueComponent: userAndTeamSelectValueComponent,
        teamsOptions: teams,
        usersOptions: users,
        teamTree: teamTree,
        isLoading: isLoadingUsersAndTeams,
        currentUser: immutableUser,
        onUsersAndTeamsMount: onUsersAndTeamsMount,
        usersAndTeamsSelectError: usersAndTeamsSelectError,
        trackUsage: trackUsage,
        showLabel: false,
        inboxId: inboxId,
        workSpace: workSpace,
        isUngatedForHelpDeskRoutingScopeFiltering: shouldUseScopeFiltering
      }), showDistributionRow && /*#__PURE__*/_jsx(DistributionSelect, {
        channelType: use,
        disabled: isDisabled,
        distribution: getDistribution(routingRules),
        onChange: newDistribution => {
          onRuleUpdateProp(
          // this is ok to do because for this to show we know that
          // `routingRules` is defined
          setDistribution(newDistribution)(routingRules));
        }
      }), !hideAvailableUserCheckbox && /*#__PURE__*/_jsx(AssignToAvailableCheckbox, {
        channelType: use,
        disabled: isDisabled,
        routingRules: routingRules,
        onRuleUpdate: onRuleUpdate
      })]
    })]
  });
};
DeprecatedRoutingRules.displayName = 'DeprecatedRoutingRules';
const checkHasObjectTypeId = props => !!props.objectTypeId && typeof props.objectTypeId === 'string';
const checkIsDeprecatedProps = props => props.shouldFetchTeams !== true && !checkHasObjectTypeId(props);

// this function is temporary to make TypeScript happy
const getRealProps = props => {
  const realProps = _objectWithoutPropertiesLoose(props, _excluded);
  return realProps;
};
const RoutingRules = props => {
  if (checkIsDeprecatedProps(props)) {
    return /*#__PURE__*/_jsx(DeprecatedRoutingRules, Object.assign({}, getRealProps(props)));
  }
  return /*#__PURE__*/_jsx(RoutingRulesFetchTeams, Object.assign({}, getRealProps(props)));
};
RoutingRules.displayName = 'RoutingRules';
export default RoutingRules;